import { Routes } from '@angular/router';

import { LibrariesComponent } from '@adw/projects/aca-content/src/lib/components/libraries/libraries.component';
import { FavoriteLibrariesComponent } from '@adw/projects/aca-content/src/lib/components/favorite-libraries/favorite-libraries.component';
import { SearchLibrariesResultsComponent } from '@adw/projects/aca-content/src/lib/components/search/search-libraries-results/search-libraries-results.component';
import { FavoritesComponent } from '@adw/projects/aca-content/src/lib/components/favorites/favorites.component';
import { RecentFilesComponent } from '@adw/projects/aca-content/src/lib/components/recent-files/recent-files.component';
import { SharedFilesComponent } from '@adw/projects/aca-content/src/lib/components/shared-files/shared-files.component';

import { CustomFilesComponent } from './components/custom-files/custom-files.component';
import { WkLoginComponent } from './components/wk-login/wk-login.component';
import { WkLogoutComponent } from './components/wk-logout/wk-logout.component';
import { WkResetPasswordComponent } from './components/wk-reset-password/wk-reset-password.component';
import { ChangePasswordComponent } from './components/change-password/change-password.component';
import {
  AppSharedRuleGuard,
  GenericErrorComponent,
} from '@alfresco/aca-shared';
import { wkProcessLoaderGuard } from './guards/wk-process-loader.guard';
import { wkPreviewRedirectGuard } from './guards/wk-preview-redirect.guard';
import { WkSearchResultsComponent } from './components/wk-search-results/wk-search-results.component';
import { TrashcanComponent } from '@adw/projects/aca-content/src/lib/components/trashcan/trashcan.component';
import { WkAppLayoutComponent } from './forked/components/layout/wk-app-layout/wk-app-layout.component';
import { DetailsComponent } from '@adw/projects/aca-content/src/lib/components/details/details.component';
import { WkAuthGuard } from './guards/wk-auth.guard';
import { ldClientResolver } from './services/ldc-route-resolver.service';
import { ViewProfileRuleGuard } from '@adw/projects/aca-content/src/lib/components/view-profile/view-profile.guard';
import { ViewProfileComponent } from '@adw/projects/aca-content/src/lib/components/view-profile/view-profile.component';
import { contentResolver } from './services/content.resolver';
import { WkSemanticSearchResultsComponent } from './components/wk-semantic-search-results/wk-semantic-search-results.component';
import { WkFeatureGuard } from './services/wk-feature-guard.service';

const recentFilesPath = 'recent-files';
const previewNodeIdPath = 'preview/:nodeId';
const taskDetailIdPath = 'task-detail/:taskId';

const previewLoader = () =>
  import('./components/custom-preview/custom-preview.module').then(
    (m) => m.CustomPreviewModule
  );

const taskDetailLoader = () =>
  import('./components/task-detail/task-detail-page.module').then(
    (m) => m.TaskDetailPageModule
  );
export const APP_ROUTES: Routes = [
  {
    path: 'login',
    component: WkLoginComponent,
    data: {
      title: 'APP.SIGN_IN',
    },
  },
  {
    path: 'reset-password',
    component: WkResetPasswordComponent,
    data: {
      title: 'RESET_PASSWORD.TITLE',
    },
  },
  {
    path: 'change-password',
    component: ChangePasswordComponent,
    data: {
      title: 'CHANGE_PASSWORD.TITLE',
    },
  },
  {
    path: 'logout',
    component: WkLogoutComponent,
    data: {
      title: 'APP.SIGN_OUT.TITLE',
    },
  },
  {
    path: 'preview/s/:id',
    loadChildren: () =>
      import(
        '@adw/projects/aca-content/src/lib/components/shared-link-view/shared-link-view.component' //shared-link-view/shared-link-view.module'
      ).then((m) => m.SharedLinkViewComponent),
  },
  {
    path: 'edit',
    loadChildren: () =>
      import('./components/editor/editor.module').then((m) => m.EditorModule),
    resolve: {
      ldcInitializationStatus: ldClientResolver,
    },
  },
  {
    path: '',
    component: WkAppLayoutComponent,
    resolve: { ldcInitializationStatus: ldClientResolver },
    children: [
      {
        path: '',
        redirectTo: `/dashboard`,
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        children: [
          {
            path: '',
            loadChildren: () =>
              import('./components/dashboard/dashboard.module').then(
                (m) => m.DashboardModule
              ),
          },
          {
            path: previewNodeIdPath,
            loadChildren: previewLoader,
            data: {
              navigateSource: 'dashboard',
            },
            resolve: {
              data: contentResolver,
            },
            canActivate: [wkPreviewRedirectGuard],
          },
        ],
      },
      {
        path: 'task-list',
        children: [
          {
            path: '',
            loadChildren: () =>
              import(
                './components/expanded-task-view/expanded-task-view.module'
              ).then((m) => m.ExpandedTaskViewModule),
          },
          {
            path: previewNodeIdPath,
            loadChildren: previewLoader,
            data: {
              navigateSource: 'task-list',
            },
            resolve: {
              data: contentResolver,
            },
            canActivate: [wkPreviewRedirectGuard],
          },
        ],
        canActivate: [wkProcessLoaderGuard],
      },
      {
        path: 'process-list',
        children: [
          {
            path: '',
            loadChildren: () =>
              import(
                './components/expanded-process-view/expanded-process-view.module'
              ).then((m) => m.ExpandedProcessViewModule),
          },
          {
            path: previewNodeIdPath,
            loadChildren: previewLoader,
            data: {
              navigateSource: 'process-list',
            },
            resolve: {
              data: contentResolver,
            },
            canActivate: [wkPreviewRedirectGuard],
          },
        ],
        canActivate: [wkProcessLoaderGuard],
      },
      {
        path: 'expanded-content',
        children: [
          {
            path: '',
            loadChildren: () =>
              import(
                './components/expanded-content/expanded-content.module'
              ).then((m) => m.ExpandedContentModule),
          },
          {
            path: previewNodeIdPath,
            loadChildren: previewLoader,
            data: {
              navigateSource: 'expanded-content',
            },
            resolve: {
              data: contentResolver,
            },
            canActivate: [wkPreviewRedirectGuard],
          },
        ],
      },
      {
        path: 'libraries',
        children: [
          {
            path: '',
            component: LibrariesComponent,
            data: {
              title: 'APP.BROWSE.LIBRARIES.MENU.MY_LIBRARIES.TITLE',
              sortingPreferenceKey: 'libraries',
            },
          },
        ],
      },
      {
        path: 'libraries/:folderId',
        children: [
          {
            path: '',
            component: CustomFilesComponent,
            data: {
              title: 'APP.BROWSE.LIBRARIES.MENU.MY_LIBRARIES.TITLE',
              sortingPreferenceKey: 'libraries-files',
            },
          },
          // deprecated, backwards compatibility with ACA 1.8
          {
            path: previewNodeIdPath,
            loadChildren: previewLoader,
            data: {
              navigateSource: 'libraries',
            },
            resolve: {
              data: contentResolver,
            },
            canActivate: [wkPreviewRedirectGuard],
          },
          {
            path: taskDetailIdPath,
            loadChildren: taskDetailLoader,
            data: {
              navigateSource: 'libraries',
            },
          },
          {
            path: 'details/:nodeId',
            children: [
              {
                path: '',
                component: DetailsComponent,
                data: {
                  navigateSource: 'personal-files',
                },
              },
              {
                path: ':activeTab',
                component: DetailsComponent,
                data: {
                  title: 'APP.BROWSE.PERSONAL.PERMISSIONS.TITLE',
                  navigateSource: 'personal-files',
                },
              },
            ],
          },
        ],
      },
      {
        path: 'favorite',
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'libraries',
          },
          {
            path: 'libraries',
            component: FavoriteLibrariesComponent,
            data: {
              title: 'APP.BROWSE.LIBRARIES.MENU.FAVORITE_LIBRARIES.TITLE',
              sortingPreferenceKey: 'favorite-libraries',
            },
          },
        ],
      },
      {
        path: 'favorite/libraries/:folderId',
        children: [
          {
            path: '',
            component: CustomFilesComponent,
            data: {
              title: 'APP.BROWSE.LIBRARIES.MENU.FAVORITE_LIBRARIES.TITLE',
              sortingPreferenceKey: 'libraries-files',
            },
          },
          {
            path: previewNodeIdPath,
            loadChildren: previewLoader,
            data: {
              navigateSource: 'favorite',
            },
            resolve: {
              data: contentResolver,
            },
            canActivate: [wkPreviewRedirectGuard],
          },
          {
            path: 'compare/:nodeId',
            loadChildren: previewLoader,
            data: {
              navigateSource: 'libraries',
            },
          },
          {
            path: 'merge/:nodeId',
            loadChildren: previewLoader,
            data: {
              navigateSource: 'libraries',
            },
          },
        ],
      },
      {
        path: 'favorites',
        data: {
          sortingPreferenceKey: 'favorites',
        },
        children: [
          {
            path: '',
            component: FavoritesComponent,
            data: {
              title: 'APP.BROWSE.FAVORITES.TITLE',
              sortingPreferenceKey: 'favorites',
            },
            // loadChildren:
            //   './components/favorites/favorites.module#AppFavoritesModule'
          },
          // deprecated, backwards compatibility with ACA 1.8
          {
            path: previewNodeIdPath,
            loadChildren: previewLoader,
            data: {
              navigateSource: 'favorites',
            },
            resolve: {
              data: contentResolver,
            },
            canActivate: [wkPreviewRedirectGuard],
          },
        ],
      },
      {
        path: recentFilesPath,
        data: {
          sortingPreferenceKey: recentFilesPath,
        },
        children: [
          {
            path: '',
            component: RecentFilesComponent,
            data: {
              title: 'APP.BROWSE.RECENT.TITLE',
            },
            // loadChildren:
            //   './components/recent-files/recent-files.module#AppRecentFilesModule'
          },
          // deprecated, backwards compatibility with ACA 1.8
          {
            path: previewNodeIdPath,
            loadChildren: previewLoader,
            data: {
              navigateSource: recentFilesPath,
            },
            resolve: {
              data: contentResolver,
            },
            canActivate: [wkPreviewRedirectGuard],
          },
        ],
      },
      {
        path: 'shared',
        children: [
          {
            path: '',
            data: {
              title: 'APP.BROWSE.SHARED.TITLE',
              sortingPreferenceKey: 'shared-files',
            },
            component: SharedFilesComponent,
            // loadChildren:
            //  './components/shared-files/shared-files.module#AppSharedFilesModule'
          },
          // deprecated, backwards compatibility with ACA 1.8
          {
            path: previewNodeIdPath,
            loadChildren: previewLoader,
            data: {
              navigateSource: 'shared',
            },
            resolve: {
              data: contentResolver,
            },
            canActivate: [wkPreviewRedirectGuard],
          },
        ],
        canActivateChild: [AppSharedRuleGuard],
        canActivate: [AppSharedRuleGuard],
      },
      {
        path: 'trashcan',
        children: [
          {
            path: '',
            component: TrashcanComponent,
          },
        ],
      },
      {
        path: 'search',
        children: [
          {
            path: '',
            component: WkSearchResultsComponent,
            data: {
              title: 'APP.BROWSE.SEARCH.TITLE',
            },
          },
          // deprecated, backwards compatibility with ACA 1.8
          {
            path: previewNodeIdPath,
            loadChildren: previewLoader,
            data: {
              navigateSource: 'search',
            },
            resolve: {
              data: contentResolver,
            },
            canActivate: [wkPreviewRedirectGuard],
          },
          {
            path: taskDetailIdPath,
            loadChildren: taskDetailLoader,
            data: {
              navigateSource: 'search',
            },
          },
        ],
      },
      {
        path: 'search-libraries',
        children: [
          {
            path: '',
            component: SearchLibrariesResultsComponent,
            data: {
              title: 'APP.BROWSE.SEARCH.TITLE',
            },
          },
        ],
      },
      {
        path: 'search-semantic',
        children: [
          {
            path: '',
            component: WkSemanticSearchResultsComponent,
            data: {
              title: 'SEARCH.SEMANTIC.TITLE',
            },
          },
        ],
        canActivate: [WkFeatureGuard],
      },
      {
        path: 'all-docs-dashboard',
        children: [
          {
            path: '',
            loadChildren: () =>
              import(
                './components/all-documents-dashboard/wk-all-documents-dashboard.module'
              ).then((m) => m.WkAllDocumentsDashboardModule),
          },
          {
            path: previewNodeIdPath,
            loadChildren: previewLoader,
            data: {
              navigateSource: 'favorite',
            },
            resolve: {
              data: contentResolver,
            },
            canActivate: [wkPreviewRedirectGuard],
          },
          {
            path: taskDetailIdPath,
            loadChildren: taskDetailLoader,
            data: {
              navigateSource: 'all-docs-dashboard',
            },
          },
          {
            path: '**',
            redirectTo: '',
          },
        ],
      },
      {
        path: 'profile',
        canActivate: [ViewProfileRuleGuard],
        component: ViewProfileComponent,
      },
      {
        path: 'nodes/:nodeId',
        children: [
          {
            path: '',
            loadChildren: () =>
              import(
                '@adw/projects/aca-content/folder-rules/src/public-api'
              ).then((m) => m.AcaFolderRulesModule),
          },
        ],
      },
      {
        path: '**',
        component: GenericErrorComponent,
      },
    ],
    canActivateChild: [WkAuthGuard],
    canActivate: [WkAuthGuard],
  },
];
